import { CountrySelector } from '@/components/common/CountrySelector';
import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import { IdentificationFormData } from '../types';

interface OperationalOfficeSectionProps {
  control: Control<IdentificationFormData>;
  showOperationalOfficeFields: boolean;
}

export function OperationalOfficeSection({
  control,
  showOperationalOfficeFields,
}: OperationalOfficeSectionProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-4'>
        <h4 className='text-md font-medium'>
          {tForms('identification.sections.operationalOffice')}
        </h4>
      </div>

      {/* Checkbox: The same address as in point 1 */}
      <FormField
        control={control}
        name='sameAddressAsContractingParty'
        render={({ field }) => (
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='sameAddressAsContractingParty'
              checked={field.value}
              onCheckedChange={field.onChange}
            />
            <label
              htmlFor='sameAddressAsContractingParty'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
            >
              {tForms('identification.fields.sameAddressAsContractingParty')}
            </label>
          </div>
        )}
      />

      {/* Operational Office fields - hidden when checkbox is checked */}
      {showOperationalOfficeFields && (
        <>
          {/* Row 1: Company Name & Address */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='operationalOfficeCompanyName'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.operationalOfficeCompanyName'
                  )}
                  name='operationalOfficeCompanyName'
                  required
                  tooltip={tForms(
                    'identification.tooltips.operationalOfficeCompanyName'
                  )}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    placeholder={tForms(
                      'identification.placeholders.operationalOfficeCompanyName'
                    )}
                    className='min-h-[44px]'
                    autoComplete='organization'
                    inputMode='text'
                  />
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='operationalOfficeAddress'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.operationalOfficeAddress'
                  )}
                  name='operationalOfficeAddress'
                  required
                  tooltip={tForms(
                    'identification.tooltips.operationalOfficeAddress'
                  )}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    placeholder={tForms(
                      'identification.placeholders.operationalOfficeAddress'
                    )}
                    className='min-h-[44px]'
                    autoComplete='street-address'
                    inputMode='text'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Row 2: Postcode & City */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='operationalOfficePostcode'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.operationalOfficePostcode'
                  )}
                  name='operationalOfficePostcode'
                  required
                  tooltip={tForms(
                    'identification.tooltips.operationalOfficePostcode'
                  )}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    placeholder={tForms(
                      'identification.placeholders.operationalOfficePostcode'
                    )}
                    className='min-h-[44px]'
                    autoComplete='postal-code'
                    inputMode='text'
                  />
                </FormFieldWrapper>
              )}
            />

            <FormField
              control={control}
              name='operationalOfficeCity'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms('identification.fields.operationalOfficeCity')}
                  name='operationalOfficeCity'
                  required
                  tooltip={tForms(
                    'identification.tooltips.operationalOfficeCity'
                  )}
                  error={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    placeholder={tForms(
                      'identification.placeholders.operationalOfficeCity'
                    )}
                    className='min-h-[44px]'
                    autoComplete='address-level2'
                    inputMode='text'
                  />
                </FormFieldWrapper>
              )}
            />
          </div>

          {/* Row 3: Country */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
            <FormField
              control={control}
              name='operationalOfficeCountry'
              render={({ field, fieldState }) => (
                <FormFieldWrapper
                  label={tForms(
                    'identification.fields.operationalOfficeCountry'
                  )}
                  name='operationalOfficeCountry'
                  required
                  tooltip={tForms(
                    'identification.tooltips.operationalOfficeCountry'
                  )}
                  error={fieldState.error?.message}
                >
                  <CountrySelector
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={tForms(
                      'identification.placeholders.operationalOfficeCountry'
                    )}
                    error={fieldState.error?.message}
                    name='operationalOfficeCountry'
                  />
                </FormFieldWrapper>
              )}
            />
            {/* Empty space for alignment */}
            <div></div>
          </div>
        </>
      )}
    </div>
  );
}
